import { ChevronRight, Shield, Award, Globe, Users, MapPin, Handshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import Seo from '../components/Seo';
import { OrganizationSchema } from '../components/SchemaMarkup';

function Partners() {
  const breadcrumbs = [
    { label: 'Partners & Affiliations', path: '/partners' },
  ];

  const partners = [
    { name: 'IATA (International Air Transport Association)', url: 'https://www.iata.org/', category: 'Industry Authority' },
    { name: 'ICAO (International Civil Aviation Organization)', url: 'https://www.icao.int/', category: 'Industry Authority' },
    { name: 'Global Travel Association', url: '#', category: 'Travel Partners' },
  ];

  const airportsServed = [
    'Mombasa International Airport (MBA)',
    'Nairobi Jomo Kenyatta International Airport (NBO)',
    'Major African Hub Airports',
    'European Gateway Airports',
    'Middle Eastern Regional Airports',
  ];

  return (
    <div className="font-sans text-black">
      <Seo
        title="Partners & Affiliations | Moreflex Aviation"
        description="Moreflex Aviation partners with IATA, ICAO, and leading travel authorities to deliver world-class VIP airport services across Mombasa, Nairobi, and international hub airports."
        path="/partners"
      />
      <OrganizationSchema />
      <Header />
      <Breadcrumb items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white py-20 overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="eyebrow justify-center mb-4 animate-fadeInTop">Trusted Network</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeInTop">
              Partners & Affiliations
            </h1>
            <p className="text-lg text-white/75 mb-8 animate-fadeIn">
              Moreflex Aviation partners with leading industry authorities and service providers
              to deliver world-class VIP airport services. Our commitment to excellence is built
              on trusted partnerships and compliance with international aviation standards.
            </p>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#73BDF2] rounded-full opacity-10 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#73BDF2] rounded-full opacity-10 transform translate-x-1/4 translate-y-1/4"></div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Left - Values */}
            <div className="animate-fadeInLeft">
              <p className="eyebrow mb-3">Standards We Live By</p>
              <h2 className="section-title mb-8">Our Commitment to Excellence</h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4 mt-1">
                    <Shield className="text-[#73BDF2]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Safety & Compliance</h3>
                    <p className="text-gray-600">
                      We adhere to international aviation safety standards and regulatory requirements
                      from ICAO and IATA guidelines.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4 mt-1">
                    <Award className="text-[#73BDF2]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Industry Recognition</h3>
                    <p className="text-gray-600">
                      Our services are recognized by leading travel and aviation industry organizations
                      for quality and professionalism.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4 mt-1">
                    <Users className="text-[#73BDF2]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Professional Team</h3>
                    <p className="text-gray-600">
                      Our staff undergoes regular training aligned with international best practices
                      for VIP airport assistance.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#73BDF2]/20 p-3 rounded-full mr-4 mt-1">
                    <Globe className="text-[#73BDF2]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Global Standards</h3>
                    <p className="text-gray-600">
                      We maintain consistent service quality across all partner airports worldwide
                      using standardized protocols.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - CTA */}
            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 flex flex-col justify-center animate-fadeInRight">
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4">Why Partner With Moreflex?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span>24/7 availability across global airports</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span>Customizable service packages for corporate clients</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span>Seamless integration with travel management systems</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span>Dedicated account management and support</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="text-[#73BDF2] mr-3 mt-1 flex-shrink-0" size={20} />
                    <span>Competitive pricing and flexible billing options</span>
                  </li>
                </ul>
              </div>

              <Link
                to="/contact"
                className="btn-primary"
              >
                Become a Partner <ChevronRight size={20} className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Partnerships Section */}
      <section className="py-20 md:py-24 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="eyebrow justify-center mb-3">Certifications</p>
            <h2 className="section-title mb-4">Industry Partners & Certifications</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We work with globally recognized aviation and travel authorities to ensure the highest
              standards of service and compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partners.map((partner, index) => (
              <div key={index} className="card-surface p-6">
                <div className="flex items-start mb-4">
                  <Handshake className="text-[#73BDF2] mr-4 mt-1 flex-shrink-0" size={24} />
                  <div>
                    <p className="text-sm text-[#73BDF2] font-semibold mb-2">{partner.category}</p>
                    <h3 className="text-lg font-bold mb-2">{partner.name}</h3>
                  </div>
                </div>
                {partner.url !== '#' && (
                  <a
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#73BDF2] hover:text-[#5ba8e0] transition-colors duration-300 flex items-center text-sm"
                  >
                    Visit Partner <ChevronRight size={16} className="ml-1" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Airports Served Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="eyebrow justify-center mb-3">Global Coverage</p>
            <h2 className="section-title mb-4">Airports We Serve</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our network spans major international airports across Africa, Europe, and the Middle East,
              with expansion ongoing to meet global demand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {airportsServed.map((airport, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl border border-gray-100 flex items-start">
                <MapPin className="text-[#73BDF2] mr-4 mt-1 flex-shrink-0" size={24} />
                <div>
                  <p className="text-gray-600">{airport}</p>
                  {index === 0 && (
                    <Link to="/services" className="text-[#73BDF2] hover:text-[#5ba8e0] text-sm mt-2 transition-colors duration-300">
                      View our services →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-black via-[#0b1f33] to-[#1a3c55] text-white overflow-hidden">
        <div className="hero-pattern absolute inset-0" />
        <div className="container relative mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Experience Premium Airport Services?</h2>
          <p className="text-lg text-white/75 mb-8 max-w-2xl mx-auto">
            Contact us today to learn more about our services or to discuss partnership opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="btn-primary"
            >
              Explore Services <ChevronRight size={20} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="btn-outline-light"
            >
              Contact Us <ChevronRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Partners;
